import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BarChart, TrendingUp, Users, AlertCircle, 
  CheckCircle, MapPin, Filter, LogOut, Activity, Zap, Banknote, Shield, DollarSign, Key
} from 'lucide-react';
import AdminCredentials from './AdminCredentials';
import AdminPricing from './AdminPricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [overview, setOverview] = useState(null);
  const [merchants, setMerchants] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [errors, setErrors] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [filters, setFilters] = useState({
    province: '',
    transaction_type: 'all',
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('mojo_admin_token');

  const menuItems = [
    { path: '/admin', icon: <BarChart />, label: 'Analytics' },
    { path: '/admin/credentials', icon: <Key />, label: 'Credentials' },
    { path: '/admin/pricing', icon: <DollarSign />, label: 'Pricing & Costs' },
  ];

  useEffect(() => {
    if (!token) {
      console.log('⚠️ No admin token found, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    console.log('✅ Admin token found, fetching analytics');
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };

    console.log('📊 Fetching analytics with token:', token?.substring(0, 20) + '...');

    try {
      console.log('📡 Calling analytics endpoints...');
      
      const [overviewRes, merchantsRes, customersRes, errorsRes, integrationsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { headers }).catch(e => {
          console.error('Overview failed:', e.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/admin/analytics/merchants`, { headers }).catch(e => {
          console.error('Merchants failed:', e.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/admin/analytics/customers`, { headers }).catch(e => {
          console.error('Customers failed:', e.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/admin/analytics/errors`, { headers }).catch(e => {
          console.error('Errors failed:', e.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/admin/analytics/integrations`, { headers }).catch(e => {
          console.error('Integrations failed:', e.response?.status);
          return { data: null };
        })
      ]);

      console.log('✅ Analytics fetched:', {
        overview: !!overviewRes.data,
        merchants: !!merchantsRes.data,
        customers: !!customersRes.data,
        errors: !!errorsRes.data,
        integrations: !!integrationsRes.data
      });

      setOverview(overviewRes.data);
      setMerchants(merchantsRes.data);
      setCustomers(customersRes.data);
      setErrors(errorsRes.data);
      setIntegrations(integrationsRes.data);
    } catch (error) {
      console.error('❌ Analytics fetch failed:', error);
      toast.error('Failed to fetch analytics');
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('⚠️ Auth error, redirecting to login');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    const params = new URLSearchParams();
    
    if (filters.transaction_type && filters.transaction_type !== 'all') {
      params.append('transaction_type', filters.transaction_type);
    }
    if (filters.province) params.append('location', filters.province);
    if (filters.start_date) params.append('start_date', new Date(filters.start_date).toISOString());
    if (filters.end_date) params.append('end_date', new Date(filters.end_date).toISOString());

    try {
      const response = await axios.get(`${API}/admin/analytics/breakdown?${params}`, { headers });
      setBreakdown(response.data);
      toast.success('Filters applied');
    } catch (error) {
      toast.error('Failed to apply filters');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mojo_admin_token');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#1B0C34]">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#1B0C34]" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#1B0C34] to-[#145539] bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
          
          {/* Navigation Tabs */}
          <nav className="flex gap-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-gradient-to-r from-[#F2B10C] to-[#d99a0a] text-white'
                    : 'text-[#1B0C34] hover:bg-white/50'
                }`}
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Routes */}
      <Routes>
        <Route path="/" element={<AnalyticsHome 
          overview={overview}
          merchants={merchants}
          customers={customers}
          errors={errors}
          integrations={integrations}
          breakdown={breakdown}
          filters={filters}
          setFilters={setFilters}
          applyFilters={applyFilters}
        />} />
        <Route path="/credentials" element={<AdminCredentials />} />
        <Route path="/pricing" element={<AdminPricing />} />
      </Routes>
    </div>
  );
};

// Extract Analytics component
const AnalyticsHome = ({ overview, merchants, customers, errors, integrations, breakdown, filters, setFilters, applyFilters }) => {
  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white">
            <CardHeader>
              <CardDescription className="text-white/70">Total Transactions</CardDescription>
              <CardTitle className="text-3xl">{overview?.totals?.transaction_count || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart className="w-8 h-8 opacity-20" />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#145539] to-[#1e7049] text-white">
            <CardHeader>
              <CardDescription className="text-white/70">Total Volume (ZAR)</CardDescription>
              <CardTitle className="text-3xl">R{overview?.totals?.total_volume?.toFixed(2) || '0.00'}</CardTitle>
            </CardHeader>
            <CardContent>
              <Banknote className="w-8 h-8 opacity-20" />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#F2B10C] to-[#d99a0a] text-[#1B0C34]">
            <CardHeader>
              <CardDescription className="text-[#1B0C34]/70">Merchants</CardDescription>
              <CardTitle className="text-3xl">{merchants?.total_merchants || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <Users className="w-8 h-8 opacity-20" />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#145539] to-[#1e7049] text-white">
            <CardHeader>
              <CardDescription className="text-white/70">Customers</CardDescription>
              <CardTitle className="text-3xl">{customers?.total_customers || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <Users className="w-8 h-8 opacity-20" />
            </CardContent>
          </Card>
        </div>

        {/* Success Rate & Errors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1B0C34]/70">Successful Transactions</span>
                  <span className="font-bold text-[#145539]">{errors?.totals?.successful || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1B0C34]/70">Success Rate</span>
                  <span className="font-bold text-2xl text-[#145539]">{errors?.totals?.success_rate || 0}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1B0C34]/70">Mocked Transactions</span>
                  <span className="font-bold text-[#F2B10C]">{errors?.totals?.mocked || 0} ({errors?.totals?.mock_rate || 0}%)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Integration Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {integrations && Object.entries(integrations).map(([name, stats]) => (
                  <div key={name} className="flex justify-between items-center p-2 bg-[#FAF5F0] rounded">
                    <span className="text-sm capitalize font-medium">{name}</span>
                    <div className="text-xs">
                      <span className="text-[#145539] font-semibold">{stats.real || 0} Real</span>
                      <span className="text-[#1B0C34]/60 ml-2">{stats.mocked || 0} Mock</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction Type Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#1B0C34]">Transaction Types (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {overview?.by_type && Object.entries(overview.by_type).map(([type, stats]) => (
                <div key={type} className="p-3 bg-[#FAF5F0] rounded-lg border border-[#F2B10C]/20">
                  <p className="text-xs text-[#1B0C34]/60 uppercase">{type.replace('_', ' ')}</p>
                  <p className="text-lg font-bold text-[#1B0C34]">{stats.count}</p>
                  <p className="text-xs text-[#145539]">R{stats.volume.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Merchant Analytics by Province */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#1B0C34]">Merchants by Province</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {merchants?.by_province && Object.entries(merchants.by_province).map(([province, stats]) => (
                <div key={province} className="flex justify-between items-center p-3 bg-[#FAF5F0] rounded">
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-[#F2B10C]" />
                    <span className="font-medium text-[#1B0C34]">{province}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#1B0C34]">{stats.count} merchants</p>
                    <p className="text-xs text-[#145539]">R{stats.total_balance.toFixed(2)} total balance</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#1B0C34]">
              <Filter className="inline mr-2" />Granular Analysis Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Transaction Type</Label>
                <Select value={filters.transaction_type} onValueChange={(val) => setFilters({...filters, transaction_type: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="card_payment">Card Payment</SelectItem>
                    <SelectItem value="wallet_topup">Wallet Top-Up</SelectItem>
                    <SelectItem value="wallet_transfer">Wallet Transfer</SelectItem>
                    <SelectItem value="money_transfer">Money Transfer</SelectItem>
                    <SelectItem value="ace_cross_border_transfer">ACE Cross-Border</SelectItem>
                    <SelectItem value="card_payout">Card Payout</SelectItem>
                    <SelectItem value="cash_withdrawal">Cash Withdrawal</SelectItem>
                    <SelectItem value="ace_token">ACE Token</SelectItem>
                    <SelectItem value="airtime_data">Airtime/Data</SelectItem>
                    <SelectItem value="insurance_premium">Insurance Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Province</Label>
                <Input
                  placeholder="e.g., Gauteng"
                  value={filters.province}
                  onChange={(e) => setFilters({...filters, province: e.target.value})}
                />
              </div>

              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>

              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>
            </div>

            <Button
              onClick={applyFilters}
              className="mt-4 bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            >
              Apply Filters
            </Button>
          </CardContent>
        </Card>

        {/* Filtered Breakdown */}
        {breakdown && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Filtered Analysis Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-[#145539]/10 rounded">
                  <p className="text-xs text-[#1B0C34]/60">Transactions</p>
                  <p className="text-2xl font-bold text-[#1B0C34]">{breakdown.summary?.total_transactions}</p>
                </div>
                <div className="p-3 bg-[#F2B10C]/10 rounded">
                  <p className="text-xs text-[#1B0C34]/60">Total Volume</p>
                  <p className="text-2xl font-bold text-[#1B0C34]">R{breakdown.summary?.total_volume?.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-[#145539]/10 rounded">
                  <p className="text-xs text-[#1B0C34]/60">Average Transaction</p>
                  <p className="text-2xl font-bold text-[#1B0C34]">R{breakdown.summary?.average_transaction?.toFixed(2)}</p>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="mt-4">
                <h3 className="font-semibold text-[#1B0C34] mb-3">Recent Transactions</h3>
                <div className="space-y-2">
                  {breakdown.recent_transactions?.slice(0, 10).map((txn, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-[#FAF5F0] rounded text-sm">
                      <div>
                        <p className="font-medium text-[#1B0C34]">{txn.transaction_type.replace('_', ' ').toUpperCase()}</p>
                        <p className="text-xs text-[#1B0C34]/60">{new Date(txn.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${txn.amount >= 0 ? 'text-[#145539]' : 'text-red-600'}`}>
                          {txn.amount >= 0 ? '+' : ''}R{Math.abs(txn.amount).toFixed(2)}
                        </p>
                        {txn.fee > 0 && <p className="text-xs text-[#1B0C34]/60">Fee: R{txn.fee.toFixed(2)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Merchant Analytics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-[#1B0C34]/70">Total Merchants:</span>
                <span className="font-bold">{merchants?.total_merchants}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#1B0C34]/70">Merchant Transactions:</span>
                <span className="font-bold">{merchants?.total_transactions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#1B0C34]/70">Transaction Volume:</span>
                <span className="font-bold text-[#145539]">R{merchants?.total_volume?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm text-[#1B0C34]/70">Commissions Earned:</span>
                <span className="font-bold text-[#F2B10C]">R{merchants?.total_commissions_earned?.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[#1B0C34]">Customer Analytics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-[#1B0C34]/70">Total Customers:</span>
                <span className="font-bold">{customers?.total_customers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#1B0C34]/70">Customer Transactions:</span>
                <span className="font-bold">{customers?.total_transactions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#1B0C34]/70">Transaction Volume:</span>
                <span className="font-bold text-[#145539]">R{customers?.total_volume?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm text-[#1B0C34]/70">Average Balance:</span>
                <span className="font-bold text-[#F2B10C]">R{customers?.average_balance?.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
};

export default AdminDashboard;
